import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, from, map, of, switchMap } from 'rxjs'
import { environment } from 'src/environments/environment'

export type LinkStatus = 'PENDING' | 'APPROVED' | 'REJECTED'

/** Situação que as telas mostram: são três, e o que o pedido pediu fica na
 *  etiqueta ao lado, não na situação. */
export type SeguroSituacao = 'PENDING' | 'APPROVED' | 'REJECTED'

export type SeguroTipo = 'VEICULO' | 'VIDA'

export interface SeguroCobertura {
  id: number
  produto_id: number
  nome: string | null
  valor: number | null
}

export interface SeguroAlteracoes {
  contrato_anterior_id: number
  cancelamento: boolean
  encerramento: boolean
  /** No cancelamento o cliente sai junto, salvo em veículo integrado na OON */
  cliente_sai: boolean
  cliente_fica_por_oon: boolean
  cliente_atual: { id: number, name: string } | null
  coberturas_adicionadas: SeguroCobertura[]
  coberturas_removidas: SeguroCobertura[]
  coberturas_mantidas: SeguroCobertura[]
  cliente_anterior: { id: number, name: string } | null
  valor_anterior: number | null
}

export interface ProductLink {
  tipo: SeguroTipo
  substitui_id: number | null
  produtos: SeguroCobertura[]
  valor_total: number
  id: number
  account_id: number
  vehicle_id: number
  customer_id: number | null
  status: LinkStatus
  /** Situação que as telas mostram */
  situation: SeguroSituacao
  /** De onde veio o contrato: criação, alteração ou pedido de cancelamento */
  request_type: 'CREATION' | 'CHANGE' | 'CANCELLATION'
  reason: string | null
  created_by: number | null
  decided_by: number | null
  decided_at: string | null
  ended_reason: 'UNLINKED' | 'REJECTED' | 'SUBSTITUIDO' | null
  ended_by: number | null
  ended_at: string | null
  /** O que o endosso muda no contrato anterior; nulo quando não é endosso */
  alteracoes: SeguroAlteracoes | null
  active: boolean
  created_at: string
  updated_at: string
  created_by_name: string | null
  decided_by_name: string | null
  ended_by_name: string | null
  vehicle: { id: number, plate: string, brand: string, model: string, type?: string | null, oon_integrated?: boolean } | null
  account: { id: number, name: string } | null
  customer: { id: number, name: string, cpf_cnpj: string, email?: string | null, phone?: string | null } | null
}

export interface ProductLinksResponse {
  results: ProductLink[]
  total_items: number
  total_pages: number
  current_page: number
  page_size: number
  has_next: boolean
  has_prev: boolean
}

export interface ProductLinksFilters {
  page?: number
  page_size?: number
  tipo?: SeguroTipo | null
  status?: string
  request_type?: string
  account_id?: number | null
  product_id?: number | null
  plate?: string
  customer?: string
  start_date?: string
  end_date?: string
}

export interface BreakdownItem {
  id: number
  name: string
  total: number
  pending: number
  approved: number
  rejected: number
  unlinked: number
}

export interface MonthlyEvolutionItem {
  month: string
  total: number
  pending: number
  approved: number
  rejected: number
  unlinked: number
}

export interface ProductLinksIndicators {
  portfolio: {
    total_vehicles_with_product: number
    active_pending: number
    active_approved: number
    total_products_value: number
    pending_value: number
    approved_value: number
  }
  history: {
    total_links: number
    pending: number
    approved: number
    rejected: number
    creation: number
    change: number
    cancellation: number
    unlinked: number
    substituted: number
    approval_rate: number | null
    avg_approval_hours: number | null
    oldest_pending_hours: number | null
  }
  by_product: BreakdownItem[]
  by_account: BreakdownItem[]
  monthly_evolution: MonthlyEvolutionItem[]
}

@Injectable({ providedIn: 'root' })
export class ProductLinksService {
  private base = `${environment.apiUrl}/seguros`

  // Contador de pendências compartilhado entre o painel e o sidebar admin
  public pendingCount$ = new BehaviorSubject<number | null>(null)

  constructor(private http: HttpClient) {}

  refreshPendingCount(): void {
    this.getPendingCount().subscribe({
      next: (data) => this.pendingCount$.next(data.pending),
      error: () => this.pendingCount$.next(null)
    })
  }

  private buildParams(filters?: ProductLinksFilters | { [key: string]: any }): HttpParams {
    let params = new HttpParams()
    if (filters) {
      Object.keys(filters).forEach((key) => {
        const value = (filters as { [key: string]: any })[key]
        if (value !== null && value !== undefined && value !== '') {
          params = params.set(key, String(value))
        }
      })
    }
    return params
  }

  getLinks(filters?: ProductLinksFilters): Observable<ProductLinksResponse> {
    return this.http.get<ProductLinksResponse>(this.base, {
      params: this.buildParams(filters),
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  approve(id: number): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.patch<{ message: string, seguro: ProductLink }>(`${this.base}/${id}/approve`, {})
  }

  reject(id: number, reason: string): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.patch<{ message: string, seguro: ProductLink }>(`${this.base}/${id}/reject`, { reason })
  }

  /** Acrescenta cobertura. Abre um pedido novo; o contrato atual segue valendo. */
  addCobertura(id: number, produtoId: number): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.post<{ message: string, seguro: ProductLink }>(
      `${this.base}/${id}/coberturas`, { produto_id: produtoId })
  }

  /** Remove cobertura. Abre um pedido novo; o contrato atual segue valendo. */
  removeCobertura(id: number, produtoId: number): Observable<{ message: string, seguro: ProductLink }> {
    return this.http.delete<{ message: string, seguro: ProductLink }>(
      `${this.base}/${id}/coberturas/${produtoId}`)
  }

  getPendingCount(): Observable<{ pending: number }> {
    return this.http.get<{ pending: number }>(`${this.base}/pending/count`, {
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  getIndicators(filters?: ProductLinksFilters): Observable<ProductLinksIndicators> {
    return this.http.get<ProductLinksIndicators>(`${this.base}/indicators`, {
      params: this.buildParams(filters),
      headers: { 'X-Skip-Loader': 'true' }
    })
  }

  export(params?: { [key: string]: any }) {
    return this.http.get(`${this.base}/export`, {
      params: this.buildParams(params),
      responseType: 'blob',
      observe: 'response' as const
    }).pipe(
      switchMap((response) => {
        const contentType = response.headers.get('content-type') || ''
        // Se o Lambda retornou JSON com base64 (padrão Traccar)
        if (contentType.includes('application/json') && response.body) {
          return from(response.body.text()).pipe(
            map((text) => {
              try {
                const json = JSON.parse(text as unknown as string)
                if (json?.data && json?.type === 'base64') {
                  const binaryString = atob(json.data)
                  const bytes = new Uint8Array(binaryString.length)
                  for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                  }
                  const blob = new Blob([bytes], {
                    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
                  })
                  return new HttpResponse<Blob>({
                    body: blob,
                    headers: response.headers,
                    status: response.status,
                    statusText: response.statusText,
                    url: response.url || undefined
                  })
                }
              } catch (_) {
                // queda para retorno original
              }
              return response
            })
          )
        }
        return of(response)
      })
    )
  }
}
